import { useAuth0 } from '@auth0/auth0-react'
import { Ability } from '@casl/ability'

import axios from 'axios'
import { useEffect, useState } from 'react'
import useSWR, { SWRResponse } from 'swr'

const fetcher = async (accessToken: string, url: string) => {
  const api = axios.create({
    baseURL:
      process.env.NODE_ENV == 'production'
        ? '/api/'
        : 'http://127.0.0.1:8787/api/',
    headers: {
      authorization: `Bearer ${accessToken}`,
    },
  })

  return await api.get(url).then((r) => r.data)
}

export const useApi = () => {
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const { getAccessTokenSilently } = useAuth0()

  useEffect(() => {
    getAccessTokenSilently().then((token) => setAccessToken(token))
  }, [getAccessTokenSilently])

  if (!accessToken) {
    return {
      isLoading: true,
    }
  }

  const api = axios.create({
    baseURL:
      process.env.NODE_ENV == 'production'
        ? '/api/'
        : 'http://127.0.0.1:8787/api/',
    headers: {
      authorization: `Bearer ${accessToken}`,
      'content-type': 'application/json',
    },
  })

  return {
    isLoading: false,
    api,
  }
}

const useGetApi = (url: string) => {
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const { getAccessTokenSilently } = useAuth0()

  useEffect(() => {
    getAccessTokenSilently().then((token) => setAccessToken(token))
  }, [getAccessTokenSilently])

  return useSWR(accessToken ? [accessToken, url] : null, fetcher)
}

const useGetApiWithCondition = (url: string, condition: boolean) => {
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const { getAccessTokenSilently } = useAuth0()

  useEffect(() => {
    getAccessTokenSilently().then((token) => setAccessToken(token))
  }, [getAccessTokenSilently])

  return useSWR(accessToken && condition ? [accessToken, url] : null, fetcher)
}

interface NewsArticles {
  status: string
  totalResults: number
  articles: {
    source: {
      name: string
    }
    author: string
    title: string
    description: string
    url: string
    urlToImage: string
    publishedAt: string
    content: string
  }[]
}

export const useGetUKNews = (): SWRResponse<NewsArticles, unknown> =>
  useGetApi('/news')

export const useAbility = () => {
  const { data } = useGetApi('/casl/abilities')

  return { ability: new Ability(data || []) }
}

export const useGetErycValidationString = (): SWRResponse<
  string | false,
  unknown
> => useGetApi('/configuration/validation_string_eryc')
export const useGetDriffieldValidationString = (): SWRResponse<
  string | false,
  unknown
> => useGetApi('/configuration/validation_string_driffield')

export const useGetSecret = (): SWRResponse<string | false, unknown> =>
  useGetApi('/configuration/secret')

export interface Location {
  ref: string
  name: string
  coordinates?: [number, number]
  gatewayIds: string[]
  gatewayMacs: string[]
}

export const useGetLocations = (): SWRResponse<Location[], unknown> =>
  useGetApi('/configuration/locations')

export interface Gateway {
  ref: string
  mac: string
  name: string
  serial: string
  lat: number
  lng: number
}

export const useGetGateways = (): SWRResponse<Gateway[], unknown> =>
  useGetApi('/configuration/gateways')

export interface DwellSummary {
  dwellTime: number
  noOfVisits: number
  percentOfAllVisits: number
}
export interface SummaryOfVisits {
  noOfVisits: number
  dwellTime: number
  newVisitors: number
  repeatVisitors: number
  dwell: {
    fiveHour: DwellSummary
    twoHour: DwellSummary
    oneHour: DwellSummary
    twentyMin: DwellSummary
    fiveMin: DwellSummary
  }
}

export const useGetMonthSummary = (
  start: number | null,
  end: number | null,
  gatewayMacs: string[] | null,
  condition: boolean,
): SWRResponse<SummaryOfVisits> => {
  const params = new URLSearchParams({
    start: start?.toString() || '',
    end: end?.toString() || '',
    gateways: gatewayMacs?.toString() || '',
  })
  return useGetApiWithCondition(
    `/query/monthSummary?${params.toString()}`,
    condition,
  )
}

export const useGetAirQuality = (
  start: number | null,
  end: number | null,
  lat: number | null,
  lng: number | null,
  interval: string,
  condition: boolean,
): SWRResponse<{ date: number; value: number }[]> =>
  useGetApiWithCondition(
    `/query/air-quality?start=${start}&end=${end}&lat=${lat}&lng=${lng}&interval=${interval}`,
    condition,
  )

export const useGetWeather = (
  start: number | null,
  end: number | null,
  gatewayMacs: string[] | null,
  interval: string,
  condition: boolean,
): SWRResponse<{ date: number; value: number }[]> => {
  const params = new URLSearchParams({
    start: start?.toString() || '',
    end: end?.toString() || '',
    gateways: gatewayMacs?.toString() || '',
    interval,
  })
  return useGetApiWithCondition(
    `/query/weather?${params.toString()}`,
    condition,
  )
}

export const useGetVisits = (
  start: number | null,
  end: number | null,
  gatewayMacs: string[] | null,
  interval: string,
  condition: boolean,
): SWRResponse<{
  visits: { [key: number]: SummaryOfVisits | null }
  kpi: { [key: string]: number }
}> => {
  const params = new URLSearchParams({
    start: start?.toString() || '',
    end: end?.toString() || '',
    gateways: gatewayMacs?.toString() || '',
    interval: interval,
  })
  return useGetApiWithCondition(`/query/visits?${params.toString()}`, condition)
}
